import { supabase } from '../lib/supabase'
import type { CreateInboxTaskInput, InboxItemType, InboxTask, InboxTaskStatus } from '../types/inbox'

const TASK_FIELDS = `
  id, title, description, status, priority, action_type, assignee_id, created_by,
  completed_by, due_at, snoozed_until, completed_at, blocked_reason,
  source_type, source_id, customer_id, property_id, contract_id, job_id,
  lead_id, proposal_id, created_at, updated_at
`

export async function fetchInboxTasks(options?: {
  assigneeId?: string
  status?: InboxTaskStatus | InboxTaskStatus[]
  sourceType?: InboxItemType
  sourceId?: string
  limit?: number
}): Promise<InboxTask[]> {
  let q = supabase.from('inbox_tasks').select(TASK_FIELDS).order('due_at', { ascending: true, nullsFirst: false })

  if (options?.assigneeId) q = q.eq('assignee_id', options.assigneeId)
  if (options?.sourceType && options?.sourceId) {
    q = q.eq('source_type', options.sourceType).eq('source_id', options.sourceId)
  }
  if (options?.status) {
    if (Array.isArray(options.status)) q = q.in('status', options.status)
    else q = q.eq('status', options.status)
  }
  if (options?.limit) q = q.limit(options.limit)

  const { data, error } = await q
  if (error) throw error
  return (data || []) as InboxTask[]
}

export async function createInboxTask(input: CreateInboxTaskInput): Promise<InboxTask> {
  const { data, error } = await supabase
    .from('inbox_tasks')
    .insert({
      title: input.title.trim(),
      description: input.description || null,
      assignee_id: input.assignee_id,
      due_at: input.due_at || null,
      priority: input.priority || 'normal',
      action_type: input.action_type || null,
      source_type: input.source_type,
      source_id: input.source_id,
      customer_id: input.customer_id || null,
      property_id: input.property_id || null,
      contract_id: input.contract_id || null,
      job_id: input.job_id || null,
      lead_id: input.lead_id || null,
      proposal_id: input.proposal_id || null,
    })
    .select(TASK_FIELDS)
    .single()
  if (error) throw error
  return data as InboxTask
}

export async function updateInboxTask(
  id: string,
  patch: Partial<
    Pick<
      InboxTask,
      | 'title'
      | 'description'
      | 'status'
      | 'priority'
      | 'action_type'
      | 'assignee_id'
      | 'due_at'
      | 'snoozed_until'
      | 'blocked_reason'
    >
  >
): Promise<InboxTask> {
  const { data, error } = await supabase
    .from('inbox_tasks')
    .update(patch)
    .eq('id', id)
    .select(TASK_FIELDS)
    .single()
  if (error) throw error
  return data as InboxTask
}

export async function completeInboxTask(id: string): Promise<InboxTask> {
  return updateInboxTask(id, { status: 'done' })
}

export async function countOpenTasksForSource(
  sourceType: InboxItemType,
  sourceId: string
): Promise<number> {
  const { count, error } = await supabase
    .from('inbox_tasks')
    .select('id', { count: 'exact', head: true })
    .eq('source_type', sourceType)
    .eq('source_id', sourceId)
    .in('status', ['open', 'in_progress', 'blocked', 'snoozed'])
  if (error) throw error
  return count || 0
}

export async function reassignTasksForSource(
  sourceType: InboxItemType,
  sourceId: string,
  newAssigneeId: string
): Promise<number> {
  const { data, error } = await supabase
    .from('inbox_tasks')
    .update({ assignee_id: newAssigneeId })
    .eq('source_type', sourceType)
    .eq('source_id', sourceId)
    .in('status', ['open', 'in_progress', 'blocked', 'snoozed'])
    .select('id')
  if (error) throw error
  return data?.length || 0
}

export function isTaskOverdue(task: InboxTask, now = new Date()): boolean {
  if (!task.due_at || task.status === 'done') return false
  if (task.status === 'snoozed' && task.snoozed_until && new Date(task.snoozed_until) > now) return false
  return new Date(task.due_at) < now
}

export function sortTasksForQueue(tasks: InboxTask[]): InboxTask[] {
  return [...tasks].sort((a, b) => {
    const aOver = isTaskOverdue(a)
    const bOver = isTaskOverdue(b)
    if (aOver !== bOver) return aOver ? -1 : 1
    const aDue = a.due_at ? new Date(a.due_at).getTime() : Number.MAX_SAFE_INTEGER
    const bDue = b.due_at ? new Date(b.due_at).getTime() : Number.MAX_SAFE_INTEGER
    if (aDue !== bDue) return aDue - bDue
    return b.created_at.localeCompare(a.created_at)
  })
}
